import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { areaStore } from "../../stores/locales/area_store"
import { make_tooltip } from "shared/tooltip"
import { tom_select_with_id } from "shared/tom_select_with_id"

/**
 * Manages the print mapper workstation panel:
 * - Loads printers and workstations when an area is selected (via areaStore)
 * - Clears the UI when the area is deselected
 * - Sorts workstation cards by name or room (natural sort)
 * - Toggles chevron icon when the printer sections collapse/expand
 * - Renders workstation cards and printer select from JSON API responses
 */
export default class extends Controller {
    static stores = [areaStore]
    static targets = ["cardContainer", "sortByNameBtn", "sortByRoomBtn", "toggleIcon", "printerSections", "printerSelectHolder", "printClassContainer"]

    connect() {
        useStore(this)
        this._sortBy = 'name'
        this._workstations = []
        this._printers = []
        this._bindCollapseEvents()
    }

    disconnect() {
        this._unbindCollapseEvents()
    }

    // Store callbacks

    onAreaStoreUpdate(area) {
        if (area && area.id) {
            // areaStore notifies twice per selection: a synchronous {id, name} write,
            // then an async enriched {id, name, region, building} write once
            // GET /api/locales/areas/describe resolves (see area_select_controller.js).
            // This controller's fetches only need area.id, so the second notification
            // is pure noise -- without this guard it re-fetches and fully re-renders
            // the workstation cards a second time, which can invalidate element
            // handles a caller (e.g. a system spec) is still reading from the first
            // render, raising StaleElementReferenceError.
            if (area.id === this._currentAreaId) {
                return
            }
            this._currentAreaId = area.id
            this._loadPrintersAndWorkstations(area.id)
        } else {
            this._currentAreaId = null
            this._clearLocationUi()
        }
    }

    /**
     * Returns the currently selected area ID, tracked via the areaStore subscription.
     * Used by legacy jQuery functions that need the location context for CRUD operations.
     */
    get currentAreaId() {
        return this._currentAreaId
    }

    /**
     * Update a single workstation card from a JSON response (used by print_mapper.js
     * after create/delete mutations).
     */
    updateWorkstationCard(workstation) {
        if (!this.hasCardContainerTarget) {
            return
        }

        const existingCard = this.cardContainerTarget.querySelector(`#${ CSS.escape(workstation.id) }`)
        if (existingCard) {
            const cardWrapper = existingCard.closest('.workstation-card')
            if (cardWrapper) {
                const newCard = this._buildWorkstationCardElement(workstation)
                cardWrapper.replaceWith(newCard)
                make_tooltip('.mapping-tooltip')
            }
        }

        // Update internal data
        const idx = this._workstations.findIndex(ws => ws.id === workstation.id)
        if (idx >= 0) {
            this._workstations[idx] = { ...this._workstations[idx], print_mappings: workstation.print_mappings }
        }
    }

    // Actions

    sortByName(event) {
        event.preventDefault()
        event.stopPropagation()
        this._sortCards('name')
    }

    sortByRoom(event) {
        event.preventDefault()
        event.stopPropagation()
        this._sortCards('room')
    }

    // Private

    _sortCards(sortBy) {
        if (!this.hasCardContainerTarget) {
            return
        }

        const container = this.cardContainerTarget
        const cards = Array.from(container.querySelectorAll('.workstation-card'))
        if (cards.length === 0) {
            return
        }

        this._sortBy = sortBy

        cards.sort((a, b) => {
            let aVal, bVal
            if (sortBy === 'room') {
                const aBody = a.querySelector('.card-body')
                const bBody = b.querySelector('.card-body')
                aVal = aBody ? (aBody.getAttribute('data-room-name') || '') : ''
                bVal = bBody ? (bBody.getAttribute('data-room-name') || '') : ''
            } else {
                const aTitle = a.querySelector('.card-title')
                const bTitle = b.querySelector('.card-title')
                aVal = aTitle ? aTitle.textContent.trim().toLowerCase() : ''
                bVal = bTitle ? bTitle.textContent.trim().toLowerCase() : ''
            }

            return this._naturalSort(aVal, bVal)
        })

        cards.forEach((card) => container.appendChild(card))

        // Update active button state
        if (this.hasSortByNameBtnTarget) {
            this.sortByNameBtnTarget.classList.toggle('active', sortBy === 'name')
        }
        if (this.hasSortByRoomBtnTarget) {
            this.sortByRoomBtnTarget.classList.toggle('active', sortBy === 'room')
        }
    }

    _naturalSort(aVal, bVal) {
        const aParts = aVal.match(/(\d+|\D+)/g) || ['']
        const bParts = bVal.match(/(\d+|\D+)/g) || ['']

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const ap = aParts[i] || ''
            const bp = bParts[i] || ''
            const aNum = parseInt(ap, 10)
            const bNum = parseInt(bp, 10)

            if (!isNaN(aNum) && !isNaN(bNum)) {
                if (aNum !== bNum) {
                    return aNum - bNum
                }
            } else {
                if (ap < bp) {
                    return -1
                }
                if (ap > bp) {
                    return 1
                }
            }
        }
        return 0
    }

    _bindCollapseEvents() {
        if (!this.hasPrinterSectionsTarget) {
            return
        }

        this._onHidden = () => {
            if (!this.hasToggleIconTarget) {
                return
            }
            this.toggleIconTarget.classList.remove('bi-chevron-up')
            this.toggleIconTarget.classList.add('bi-chevron-down')
        }

        this._onShown = () => {
            if (!this.hasToggleIconTarget) {
                return
            }
            this.toggleIconTarget.classList.remove('bi-chevron-down')
            this.toggleIconTarget.classList.add('bi-chevron-up')
        }

        this.printerSectionsTarget.addEventListener('hidden.bs.collapse', this._onHidden)
        this.printerSectionsTarget.addEventListener('shown.bs.collapse', this._onShown)
    }

    _unbindCollapseEvents() {
        if (!this.hasPrinterSectionsTarget) {
            return
        }

        if (this._onHidden) {
            this.printerSectionsTarget.removeEventListener('hidden.bs.collapse', this._onHidden)
        }
        if (this._onShown) {
            this.printerSectionsTarget.removeEventListener('shown.bs.collapse', this._onShown)
        }
    }

    async _loadPrintersAndWorkstations(areaId) {
        try {
            const [printersResponse, workstationsResponse] = await Promise.all([
                fetch(`/api/print_mappings/printers?area_id=${ encodeURIComponent(areaId) }`),
                fetch(`/api/print_mappings/workstations?area_id=${ encodeURIComponent(areaId) }`)
            ])

            if (!printersResponse.ok || !workstationsResponse.ok) {
                console.error('Failed to load print mapping data')
                return
            }

            const printersData = await printersResponse.json()
            const workstationsData = await workstationsResponse.json()

            this._printers = printersData.printers || []
            this._workstations = workstationsData.workstations || []

            this._renderPrinterSelect(this._printers)
            this._renderWorkstationCards(this._workstations)
        } catch (error) {
            console.error('Error loading print mapping data:', error)
        }
    }

    _renderPrinterSelect(printers) {
        const holder = this.hasPrinterSelectHolderTarget
            ? this.printerSelectHolderTarget
            : document.getElementById('printer_selection_holder')
        if (!holder) {
            return
        }

        let html = '<select id="printer_select" class="form-select">'
        html += '<option value="">Select printer</option>'
        printers.forEach(printer => {
            const typeName = printer.device_type_name ? ` (${ printer.device_type_name })` : ''
            html += `<option value="${ this._escapeAttr(printer.id) }">${ this._escapeHtml(printer.name) }${ this._escapeHtml(typeName) }</option>`
        })
        html += '</select>'

        holder.innerHTML = html
        tom_select_with_id('printer_select')
    }

    _renderWorkstationCards(workstations) {
        const container = this.hasCardContainerTarget
            ? this.cardContainerTarget
            : document.getElementById('print-mapping-workstation-card-container')
        if (!container) {
            return
        }

        container.innerHTML = ''

        if (workstations.length === 0) {
            return
        }

        // Sort workstations according to current sort preference
        const sorted = [...workstations].sort((a, b) => {
            let aVal, bVal
            if (this._sortBy === 'room') {
                aVal = (a.room_name || '').toLowerCase()
                bVal = (b.room_name || '').toLowerCase()
            } else {
                aVal = (a.name || '').toLowerCase()
                bVal = (b.name || '').toLowerCase()
            }
            return this._naturalSort(aVal, bVal)
        })

        sorted.forEach(ws => {
            const card = this._buildWorkstationCardElement(ws)
            container.appendChild(card)
        })

        make_tooltip('.mapping-tooltip')
    }

    _buildWorkstationCardElement(ws) {
        const wrapper = document.createElement('div')
        wrapper.className = 'col-sm-12 col-md-6 col-lg-4 workstation-card'

        const mappings = ws.print_mappings || []

        const mappingsHtml = mappings.map(m => {
            const printClassName = this._escapeHtml(m.print_class_name || 'Unknown')
            const printerName = this._escapeHtml(m.printer_name || 'Unknown')
            return `<div class="d-flex align-items-center justify-content-between py-1 mapping-tooltip workstation-badge" data-bs-toggle="tooltip" data-bs-placement="left" title="Printer: ${ printerName }">
                <span class="small">${ printClassName }</span>
                <button type="button" class="btn-close btn-close-sm ms-2 print-mapping-workstation-label-button" value="${ this._escapeAttr(m.id) }" aria-label="Remove ${ printClassName }" style="font-size: 0.6rem;"></button>
            </div>`
        }).join('')

        // Room name shown as a badge on the card, always visible
        const roomNameHtml = ws.room_name
            ? `<span class="badge bg-secondary flex-shrink-0">${ this._escapeHtml(ws.room_name) }</span>`
            : ''

        wrapper.innerHTML = `
            <div class="card mb-3">
                <div class="card-body add_class_button" id="${ this._escapeAttr(ws.id) }" style="cursor: pointer;" data-room-name="${ this._escapeAttr((ws.room_name || '').toLowerCase()) }">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                        <h6 class="card-title mb-0 text-truncate flex-grow-1" style="min-width: 0;" title="${ this._escapeAttr(ws.name) }">${ this._escapeHtml(ws.name) }</h6>
                        <div class="d-flex align-items-center gap-1 flex-shrink-0 ms-2">
                            ${ roomNameHtml }
                            <div class="workstation-spinner d-none">
                                <div class="spinner-border spinner-border-sm text-primary" role="status">
                                    <span class="visually-hidden">Loading...</span>
                                </div>
                            </div>
                            <i class="bi bi-plus-lg text-muted" style="font-size: 0.9rem;"></i>
                        </div>
                    </div>
                    <div class="border-top mt-1 pt-1">
                        ${ mappingsHtml }
                    </div>
                </div>
            </div>
        `
        return wrapper
    }

    _clearLocationUi() {
        // Clear printer select
        const printerHolder = this.hasPrinterSelectHolderTarget
            ? this.printerSelectHolderTarget
            : document.getElementById('printer_selection_holder')
        if (printerHolder) {
            const printerSelect = printerHolder.querySelector('#printer_select')
            if (printerSelect?.tomselect) {
                printerSelect.tomselect.destroy()
            }
            printerHolder.innerHTML = ''
        }

        // Clear print class container
        const printClassContainer = this.hasPrintClassContainerTarget
            ? this.printClassContainerTarget
            : document.getElementById('available_trey_selection_button_group')
        if (printClassContainer) {
            printClassContainer.innerHTML = ''
        }

        // Clear workstation cards
        const cardContainer = this.hasCardContainerTarget
            ? this.cardContainerTarget
            : document.getElementById('print-mapping-workstation-card-container')
        if (cardContainer) {
            cardContainer.innerHTML = ''
        }

        this._workstations = []
        this._printers = []
    }

    _escapeHtml(str) {
        if (!str) {
            return ''
        }
        const div = document.createElement('div')
        div.textContent = str
        return div.innerHTML
    }

    _escapeAttr(str) {
        if (!str) {
            return ''
        }
        return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;')
                          .replace(/>/g, '&gt;')
    }
}
